import { PATH } from './path';

/**
 * TR-493 — Proactive In-App Messages via Crisp (merchant-facing).
 * Static scenario matrix. Source-of-truth: spec.md §6 (D2) + ticket §5.x.
 *
 * Mỗi scenario gắn (path mục tiêu + cách match) với 1 trigger và độ trễ tối thiểu.
 * Message là local-only (vẽ trong chatbox Crisp, không tạo conversation tới khi merchant reply).
 */

export type ProactiveTrigger = 'dwell' | 'leaveIntent' | 'session';
export type ProactiveMatch = 'exact' | 'prefix';

export interface IProactiveScenario {
  /** Khóa duy nhất — dùng cho sessionStorage `crisp_proactive_<id>` + impression event. */
  id: string;
  /** Path mục tiêu (trỏ `PATH`). Với `session` trigger dùng `'*'` (không match theo path). */
  path: string;
  /** `exact` = `pathname === path`; `prefix` = path hoặc sub-path `path/...`. */
  match: ProactiveMatch;
  trigger: ProactiveTrigger;
  /** Độ trễ tối thiểu trước khi show (ms). Bắt buộc ≥ {@link PROACTIVE_MIN_DELAY_MS} (P0-4). */
  delayMs: number;
  /** 2–3 biến thể; chọn ngẫu nhiên 1 khi show (P0-5). */
  messages: string[];
  /** Chỉ show khi Dashboard thực sự chưa có shipment (P0-8). */
  requiresEmptyState?: boolean;
}

/** Không scenario nào được hiện sớm hơn mốc này (P0-4). */
export const PROACTIVE_MIN_DELAY_MS = 10_000;

/** ID scenario fallback chung — chỉ show nếu chưa có proactive nào fire trong session (#13). */
export const GENERIC_FALLBACK_ID = 'generic-fallback';

export const PROACTIVE_SCENARIOS: IProactiveScenario[] = [
  // #1 — Subscription Plans (dwell ≥30s, exact)
  {
    id: 'plan-dwell',
    path: PATH.PLAN,
    match: 'exact',
    trigger: 'dwell',
    delayMs: 30_000,
    messages: [
      "Not sure which plan fits your store? I can help you pick the right one. 👋",
      "Comparing plans? Tell me your order volume and I'll suggest the best fit.",
      "Questions about what's included in each plan? Just ask - I'm here.",
    ],
  },
  // #2 — Subscription Plans, leave intent (P0-7, exact)
  {
    id: 'plan-leave',
    path: PATH.PLAN,
    match: 'exact',
    trigger: 'leaveIntent',
    delayMs: PROACTIVE_MIN_DELAY_MS,
    messages: [
      "Before you go - any quick question about pricing? I'm right here.",
      "Wait! Not sure about upgrading? Let me clear things up in a sec.",
      "Leaving so soon? Happy to help you find a plan that works.",
    ],
  },
  // #3 — Onboarding wizard (dwell ≥45s)
  {
    id: 'welcome-dwell',
    path: PATH.WELCOME,
    match: 'prefix',
    trigger: 'dwell',
    delayMs: 45_000,
    messages: [
      "Setting things up? If anything's unclear, just ask — I'll walk you through it.",
      "Need a hand getting started? I can guide you step by step.",
      "Stuck on setup? Tell me where you are and I'll help you move forward.",
    ],
  },
  // #4 — Tracking Page customization (dwell ≥60s, exact)
  {
    id: 'tracking-page-dwell',
    path: PATH.TRACKING_PAGE,
    match: 'exact',
    trigger: 'dwell',
    delayMs: 60_000,
    messages: [
      "Want your tracking page to match your brand? I can share a few quick tips.",
      "Customizing your tracking page? Ask me anything about styling or branding.",
      "Need help making your tracking page look great? I'm here.",
    ],
  },
  // #5 — Tracking Configuration (advanced) (dwell ≥60s)
  {
    id: 'tracking-config-dwell',
    path: PATH.TRACKING_CONFIGURATION_PAGE,
    match: 'prefix',
    trigger: 'dwell',
    delayMs: 60_000,
    messages: [
      "Advanced settings can be tricky - want me to explain any of these options?",
      "Not sure what a setting does? Ask me and I'll break it down.",
      "Configuring your tracking page? I can help you get it right.",
    ],
  },
  // #6 — Estimated Delivery Date (dwell ≥45s)
  {
    id: 'edd-dwell',
    path: PATH.POST_PURCHASE_EDD,
    match: 'prefix',
    trigger: 'dwell',
    delayMs: 45_000,
    messages: [
      "Setting up delivery estimates? I can explain how EDD works for your store.",
      "Want accurate delivery dates for your customers? Let me help you configure EDD.",
      "Questions about estimated delivery dates? Just ask 👋",
    ],
  },
  // #7 — Notifications setup (dwell ≥40s)
  {
    id: 'notifications-dwell',
    path: PATH.NOTIFICATIONS,
    match: 'prefix',
    trigger: 'dwell',
    delayMs: 40_000,
    messages: [
      "Need a hand setting up shipping notifications? I can guide you.",
      "Want to send the right alerts at the right time? Ask me about notification rules.",
      "Setting up emails? I'm here if you get stuck.",
    ],
  },
  // #8 — Email sender (Gmail OAuth) (dwell ≥30s)
  {
    id: 'sender-email-dwell',
    path: PATH.SENDER_EMAIL,
    match: 'prefix',
    trigger: 'dwell',
    delayMs: 30_000,
    messages: [
      "Connecting your sender email? I can help if the setup gets confusing.",
      "Trouble linking your email? Let me walk you through it.",
      "Questions about sending emails from your own address? Just ask.",
    ],
  },
  // #9 — Settings / Integrations (dwell ≥30s)
  {
    id: 'settings-dwell',
    path: PATH.SETTINGS,
    match: 'prefix',
    trigger: 'dwell',
    delayMs: 30_000,
    messages: [
      "Looking to connect another app? Let me know what you're trying to integrate.",
      "Need help with integrations or webhooks? I'm here.",
      "Questions about your app settings? Ask away 👋",
    ],
  },
  // #10 — Bundle / Add-ons (dwell ≥25s, exact)
  {
    id: 'bundle-dwell',
    path: PATH.PLAN_BUNDLE,
    match: 'exact',
    trigger: 'dwell',
    delayMs: 25_000,
    messages: [
      "Curious about add-ons like Returns or PayPal? I can explain what each does.",
      "Want to know if an add-on is right for your store? Ask me.",
      "Questions about bundles? Happy to help you decide.",
    ],
  },
  // #11 — Dashboard, empty state (P0-8, dwell ≥30s, exact)
  {
    id: 'dashboard-empty',
    path: PATH.DASHBOARD,
    match: 'exact',
    trigger: 'dwell',
    delayMs: 30_000,
    requiresEmptyState: true,
    messages: [
      "I notice no shipments are showing yet - want help importing your first orders?",
      "New here? Let's get your first orders tracked - I can show you how.",
      "Looks like you're just getting started. Need a hand setting things up?",
    ],
  },
  // #12 — Analytics (dwell ≥30s)
  {
    id: 'analytics-dwell',
    path: PATH.ANALYTICS,
    match: 'prefix',
    trigger: 'dwell',
    delayMs: 30_000,
    messages: [
      "Want help reading your shipment analytics? I can explain the key numbers.",
      "Questions about your fulfillment performance? Ask me anything.",
      "Not sure what a metric means? I'm here to help.",
    ],
  },
  // #13 — Generic fallback, any page (session ≥90s + chưa có message nào)
  {
    id: GENERIC_FALLBACK_ID,
    path: '*',
    match: 'prefix',
    trigger: 'session',
    delayMs: 90_000,
    messages: [
      "Hey 👋 just checking in - anything I can help you with today?",
      "Need help with anything? I'm one message away.",
      "Hi there! Let me know if you have any questions.",
    ],
  },
];
